import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { siteAdminSectionPresentation } from '@/config/siteAdminSectionPresentation';

interface HubIndexToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  onFilter?: () => void;
  onClear?: () => void;
  hasFilters?: boolean;
  children?: ReactNode;
}

export function HubIndexToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar…',
  onFilter,
  onClear,
  hasFilters,
  children,
}: HubIndexToolbarProps) {
  return (
    <div className="hub-index-toolbar">
      <div className="hub-index-search">
        <span className="material-symbols-outlined hub-index-search-icon" aria-hidden="true">
          search
        </span>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="hub-index-search-input"
          aria-label={searchPlaceholder}
        />
      </div>
      {onFilter && (
        <button type="button" className="hub-index-filter-btn" onClick={onFilter}>
          <span className="material-symbols-outlined text-[1.1rem]" aria-hidden="true">
            filter_list
          </span>
          Filtrar
        </button>
      )}
      {hasFilters && onClear ? (
        <button type="button" className="hub-index-filter-btn" onClick={onClear}>
          Limpiar
        </button>
      ) : null}
      {children}
    </div>
  );
}

interface HubPageHeaderProps {
  title: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
  toolbar?: ReactNode;
}

export function HubPageHeader({
  title,
  subtitle,
  backTo = '/administracion',
  backLabel = 'Volver',
  actions,
  toolbar,
}: HubPageHeaderProps) {
  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-start gap-4">
        {backTo && (
          <Link to={backTo} className="lib-index-back" aria-label={backLabel}>
            <span className="material-symbols-outlined text-[1.35rem]" aria-hidden="true">
              arrow_back
            </span>
          </Link>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="lib-index-title">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-dash-on-surface-variant md:text-base">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {toolbar}
    </header>
  );
}

export function HubPageShell({
  bemClass,
  children,
}: {
  bemClass: string;
  children: ReactNode;
}) {
  return <div className={`${bemClass} lms-site-admin-hub-page font-sans`}>{children}</div>;
}

export function SiteAdminSectionCard({
  label,
  links,
  description,
}: {
  label: string | null;
  links: { label: string; to: string }[];
  description?: string | null;
}) {
  const presentation = siteAdminSectionPresentation(label);
  const sectionDescription = description ?? presentation.description;
  const tone = presentation.tone;

  return (
    <article className="lms-site-admin-section">
      <div className="lms-site-admin-section__header">
        <div className={`lms-site-admin-section__icon-wrap lms-site-admin-section__icon-wrap--${tone}`}>
          <span
            className={`material-symbols-outlined lms-site-admin-section__icon lms-site-admin-section__icon--${tone}`}
            aria-hidden="true"
          >
            {presentation.icon}
          </span>
        </div>
        <div className="lms-site-admin-section__content">
          {label && <h2 className="lms-site-admin-section__title">{label}</h2>}
          {sectionDescription && (
            <p className="lms-site-admin-section__description">{sectionDescription}</p>
          )}
        </div>
      </div>
      <div className="lms-site-admin-section__links">
        {links.map((link) => (
          <Link key={link.to} to={link.to} className="lms-site-admin-section__link">
            <span
              className="material-symbols-outlined lms-site-admin-section__link-icon"
              aria-hidden="true"
            >
              link
            </span>
            <span className="lms-site-admin-section__link-label">{link.label}</span>
          </Link>
        ))}
      </div>
    </article>
  );
}

export function CatalogCard({
  title,
  description,
  to,
  meta,
}: {
  title: string;
  description?: string;
  to: string;
  meta?: ReactNode;
}) {
  return (
    <Link to={to} className="catalog-card block">
      <h3 className="font-semibold text-dash-primary">{title}</h3>
      {description && <p className="mt-1 text-sm text-dash-on-surface-variant">{description}</p>}
      {meta && <div className="mt-3 text-xs text-dash-outline">{meta}</div>}
    </Link>
  );
}
